import type { Program } from '@compass/client/types/program';
import type { Team } from '@compass/client/types/team';
import { programTeamService } from '@services/api';
import { queryKeys } from '@services/query.config';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';

import { useStableFold } from '@/hooks/internal/useStableFold';

/**
 * Merge per-program team lists into one list: deduped by team id (a team can
 * serve several programs), teams without an id dropped, sorted by name so the
 * team switcher reads the same regardless of which program answered first.
 *
 * Pure function — exported for unit tests.
 */
export function mergeProgramTeams(lists: (Team[] | undefined)[]): Team[] {
  const byId = new Map<string, Team>();
  for (const list of lists) {
    for (const team of list ?? []) {
      if (team.id && !byId.has(team.id)) byId.set(team.id, team);
    }
  }
  return [...byId.values()].sort((a, b) =>
    (a.name ?? a.id ?? '').localeCompare(b.name ?? b.id ?? ''),
  );
}

/**
 * Pick the programs to ask ListProgramTeams for so that every team linked to
 * `programs` is listed once. ListProgramTeams returns exactly the program's
 * `teams[]` (minus deleted teams), which `GetCurrentUserPrograms` already
 * carries, so a program without linked teams is skipped and a program whose
 * teams are all covered by an earlier one adds nothing. Many programs share a
 * team, so this keeps an org admin's console from one call per program.
 *
 * Pure function — exported for unit tests.
 */
export function pickTeamSourcePrograms(programs: Program[]): Program[] {
  const covered = new Set<string>();
  const sources: Program[] = [];
  for (const program of programs) {
    const uncovered = (program.teams ?? []).filter(id => !covered.has(id));
    if (!program.id || uncovered.length === 0) continue;
    uncovered.forEach(id => covered.add(id));
    sources.push(program);
  }
  return sources;
}

/**
 * The teams the Workforce Console can show: every team linked to one of the
 * given programs (the programs where the user may use the console, from
 * `useWorkforceEligiblePrograms`). ListProgramTeams calls go only to the
 * programs `pickTeamSourcePrograms` picks, sharing the cache key with
 * `useProgramTeams`.
 */
export function useWorkforceTeams(
  organizationId: string | null | undefined,
  programs: Program[],
): { teams: Team[]; isLoading: boolean } {
  const sources = useMemo(() => pickTeamSourcePrograms(programs), [programs]);
  const queries = useQueries({
    queries: sources.map(program => ({
      queryKey: queryKeys.teams.byProgram(organizationId ?? '', program.id ?? ''),
      queryFn: () =>
        programTeamService.listProgramTeams({
          organization_id: organizationId ?? '',
          program_id: program.id ?? '',
        }),
      enabled: !!organizationId && !!program.id,
      staleTime: 2 * 60 * 1000,
      gcTime: 5 * 60 * 1000,
    })),
  });

  const teams = useStableFold(
    queries,
    q => q.data?.teams?.length ?? 0,
    qs => mergeProgramTeams(qs.map(q => q.data?.teams as Team[] | undefined)),
  );

  return { teams, isLoading: queries.some(q => q.isLoading) };
}
